import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { 
  Search, 
  FileText,
  Download,
  Send,
  DollarSign,
  AlertCircle,
  Trash2,
  Loader2,
  Lock
} from 'lucide-react';
import { invoicesAPI, emailAPI, projectsAPI, companySettingsAPI, customersAPI } from '../services/api';
import AddInvoiceDialog from '../components/AddInvoiceDialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { generateQuotePDF, generateInvoicePDF } from '../utils/pdfGenerator';

const Invoicing = () => {
  const [invoicesData, setInvoicesData] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sending, setSending] = useState(false);
  const { toast } = useToast();
  const { canCreate, canEdit, canDelete } = useAuth();

  useEffect(() => {
    fetchInvoices();
  }, [selectedStatus]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const [invoicesRes, statsRes] = await Promise.all([
        invoicesAPI.getAll({ status: selectedStatus }),
        invoicesAPI.getStats()
      ]);
      setInvoicesData(invoicesRes.data);
      setStats(statsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Fout bij ophalen facturen:', error);
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'all': 'Alle Statussen',
      'draft': 'Concept',
      'pending': 'In Afwachting',
      'partial': 'Deels Betaald',
      'paid': 'Betaald',
      'overdue': 'Verlopen'
    };
    return labels[status] || status;
  };

  const getTypeLabel = (type) => {
    const labels = {
      'quote': 'Offerte',
      'invoice': 'Factuur'
    };
    return labels[type] || type;
  };

  const statuses = ['all', 'draft', 'pending', 'partial', 'paid', 'overdue'];

  const filteredInvoices = invoicesData.filter(invoice => {
    const matchesSearch = invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.customer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalInvoiced = stats?.totalInvoiced || 0;
  const totalPaid = stats?.totalPaid || 0;
  const totalOutstanding = stats?.totalOutstanding || 0;
  const overdueCount = stats?.overdueCount || 0;

  const handleDownload = async (invoice) => {
    const isQuote = invoice.type === 'quote' || invoice.invoiceNumber?.startsWith('OFF-') || invoice.invoiceNumber?.startsWith('O-');
    
    // Get company settings and logo
    let company = {};
    let logo = null;
    try {
      const [settingsRes, logoRes] = await Promise.all([
        companySettingsAPI.get(),
        companySettingsAPI.getLogo()
      ]);
      company = settingsRes.data;
      logo = logoRes.data?.logo || null;
    } catch (error) {
      console.log('Could not fetch company settings, using defaults');
    }
    
    // Get customer details - first check invoice, then fetch from customers collection
    let customerData = {
      customerContact: invoice.customerContact || '',
      customerEmail: invoice.customerEmail || '',
      customerPhone: invoice.customerPhone || '',
      customerAddress: invoice.customerAddress || ''
    };
    
    // If customer details not on invoice, try to fetch from customers collection
    if (!customerData.customerAddress && invoice.customer) {
      try {
        const customersRes = await customersAPI.getAll();
        const customers = customersRes.data || [];
        // Find customer by name
        const matchedCustomer = customers.find(c => 
          c.name?.toLowerCase() === invoice.customer?.toLowerCase() ||
          c.company?.toLowerCase() === invoice.customer?.toLowerCase()
        );
        if (matchedCustomer) {
          customerData = {
            customerContact: matchedCustomer.name || invoice.customer,
            customerEmail: matchedCustomer.email || '',
            customerPhone: matchedCustomer.phone || '',
            customerAddress: matchedCustomer.address || ''
          };
        }
      } catch (error) {
        console.log('Could not fetch customer details');
      }
    }
    
    // Use items and crew stored directly on the invoice
    let items = invoice.items || [];
    let crew = invoice.crew || [];
    let days = invoice.days || 1;
    let productionDate = invoice.issueDate;
    let productionEndDate = null;
    
    // If items not stored on invoice, try to get from project
    if (items.length === 0 && invoice.projectId) {
      try {
        const projectRes = await projectsAPI.getById(invoice.projectId);
        const project = projectRes.data;
        
        // Calculate days from project dates
        if (project.startDate && project.endDate) {
          const start = new Date(project.startDate);
          const end = new Date(project.endDate);
          days = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
          productionDate = project.startDate;
          productionEndDate = project.endDate;
        }
        
        // Get equipment items from project
        if (project.equipment && Array.isArray(project.equipment)) {
          items = project.equipment.map(eq => ({
            name: eq.name || 'Item',
            category: eq.category || 'APPARATUUR',
            quantity: eq.quantity || 1,
            pricePerDay: eq.pricePerDay || eq.price || 0
          }));
        }
        
        // Get crew from project
        if (project.crew && Array.isArray(project.crew)) {
          crew = project.crew.map(c => ({
            name: c.name || 'Medewerker',
            role: c.role || '',
            ratePerDay: c.ratePerDay || 0
          }));
        }
      } catch (error) {
        console.log('Could not fetch project details');
      }
    }
    
    // Fallback: if still no items, create a simple line item
    if (items.length === 0 && crew.length === 0) {
      items = [{
        name: invoice.project || 'Diensten',
        category: 'DIENSTEN',
        quantity: 1,
        pricePerDay: invoice.amount || 0
      }];
    }
    
    // Calculate totals
    const subtotal = invoice.subtotal || invoice.amount || 0;
    const staffelDiscount = invoice.staffelDiscount || 0;
    const discountAmount = invoice.discountAmount || (subtotal * staffelDiscount / 100);
    const totalExclBtw = invoice.amount || (subtotal - discountAmount);
    const btwAmount = totalExclBtw * 0.21;
    const totalInclBtw = totalExclBtw + btwAmount;
    
    const pdfData = {
      // Quote fields
      quoteNumber: invoice.invoiceNumber,
      quoteDate: invoice.issueDate,
      // Invoice fields  
      invoiceNumber: invoice.invoiceNumber,
      invoiceDate: invoice.issueDate,
      // Customer details (use fetched data)
      customer: invoice.customer,
      customerContact: customerData.customerContact || invoice.customer || '',
      customerEmail: customerData.customerEmail || '',
      customerPhone: customerData.customerPhone || '',
      customerAddress: customerData.customerAddress || '',
      // Production dates
      productionDate: invoice.startDate || productionDate,
      productionEndDate: invoice.endDate || productionEndDate,
      location: invoice.location || '',
      items: items,
      crew: crew,
      days: days,
      // Totals
      subtotal: subtotal,
      staffelDiscount: staffelDiscount,
      staffelDetails: invoice.staffelDetails || [],
      discountAmount: discountAmount,
      totalExclBtw: totalExclBtw,
      btwAmount: btwAmount,
      totalInclBtw: totalInclBtw,
      // Company settings
      logo: logo,
      company: company
    };
    
    let doc;
    if (isQuote) {
      doc = generateQuotePDF(pdfData);
    } else {
      doc = generateInvoicePDF(pdfData);
    }
    
    doc.save(`${invoice.invoiceNumber}.pdf`);
    
    toast({
      title: "Gelukt",
      description: "PDF succesvol gedownload"
    });
  };

  const openSendDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setRecipientEmail('');
    setSendDialogOpen(true);
  };

  const handleSend = async () => {
    if (!recipientEmail) {
      toast({ title: "Fout", description: "Voer een e-mailadres in", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      const invoice = selectedInvoice;
      const isQuote = invoice.type === 'quote' || invoice.invoiceNumber?.startsWith('OFF-');
      
      // Get company settings for PDF
      let company = {}, logo = null;
      try {
        const [settingsRes, logoRes] = await Promise.all([companySettingsAPI.get(), companySettingsAPI.getLogo()]);
        company = settingsRes.data;
        logo = logoRes.data?.logo || null;
      } catch (e) {}

      // Get customer data
      let customerData = { customerContact: invoice.customerContact || '', customerEmail: invoice.customerEmail || '', customerPhone: invoice.customerPhone || '', customerAddress: invoice.customerAddress || '' };
      if (!customerData.customerAddress && invoice.customer) {
        try {
          const customersRes = await customersAPI.getAll();
          const matched = customersRes.data?.find(c => c.name?.toLowerCase() === invoice.customer?.toLowerCase());
          if (matched) customerData = { customerContact: matched.name, customerEmail: matched.email || '', customerPhone: matched.phone || '', customerAddress: matched.address || '' };
        } catch (e) {}
      }

      let items = invoice.items || [], crew = invoice.crew || [], days = invoice.days || 1;
      let productionDate = invoice.issueDate, productionEndDate = null;

      if (items.length === 0 && invoice.projectId) {
        try {
          const projectRes = await projectsAPI.getById(invoice.projectId);
          const project = projectRes.data;
          if (project.startDate && project.endDate) {
            days = Math.max(1, Math.ceil((new Date(project.endDate) - new Date(project.startDate)) / (1000 * 60 * 60 * 24)) + 1);
            productionDate = project.startDate;
            productionEndDate = project.endDate;
          }
          if (project.equipment) items = project.equipment.map(eq => ({ name: eq.name || 'Item', category: eq.category || 'APPARATUUR', quantity: eq.quantity || 1, pricePerDay: eq.pricePerDay || eq.price || 0 }));
          if (project.crew) crew = project.crew.map(c => ({ name: c.name || 'Medewerker', role: c.role || '', ratePerDay: c.ratePerDay || 0 }));
        } catch (e) {}
      }
      if (items.length === 0 && crew.length === 0) items = [{ name: invoice.project || 'Diensten', category: 'DIENSTEN', quantity: 1, pricePerDay: invoice.amount || 0 }];

      const subtotal = invoice.subtotal || invoice.amount || 0;
      const staffelDiscount = invoice.staffelDiscount || 0;
      const discountAmount = invoice.discountAmount || (subtotal * staffelDiscount / 100);
      const totalExclBtw = invoice.amount || (subtotal - discountAmount);

      const pdfData = {
        quoteNumber: invoice.invoiceNumber, quoteDate: invoice.issueDate,
        invoiceNumber: invoice.invoiceNumber, invoiceDate: invoice.issueDate,
        customer: invoice.customer, ...customerData,
        productionDate: invoice.startDate || productionDate,
        productionEndDate: invoice.endDate || productionEndDate,
        location: invoice.location || '', items, crew, days,
        subtotal, staffelDiscount, staffelDetails: invoice.staffelDetails || [],
        discountAmount, totalExclBtw, btwAmount: totalExclBtw * 0.21, totalInclBtw: totalExclBtw * 1.21,
        logo, company
      };

      // Generate PDF
      const doc = isQuote ? generateQuotePDF(pdfData) : generateInvoicePDF(pdfData);
      const pdfBase64 = doc.output('datauristring').split(',')[1];

      await emailAPI.sendInvoice({
        recipient_email: recipientEmail,
        invoice_number: invoice.invoiceNumber,
        invoice_id: invoice.id,
        customer_name: invoice.customer,
        project_name: invoice.project,
        amount: invoice.amount,
        due_date: new Date(invoice.dueDate).toLocaleDateString('nl-NL'),
        issue_date: new Date(invoice.issueDate).toLocaleDateString('nl-NL'),
        pdf_base64: pdfBase64,
        pdf_filename: invoice.invoiceNumber + '.pdf'
      });
      
      toast({ title: "Gelukt", description: (isQuote ? 'Offerte' : 'Factuur') + ' verstuurd naar ' + recipientEmail });
      setSendDialogOpen(false);
    } catch (error) {
      console.error('Fout bij versturen:', error);
      toast({ title: "Fout", description: error.response?.data?.detail || "Kan niet versturen", variant: "destructive" });
    } finally { setSending(false); }
  };


  const handleDelete = async (invoiceId) => {
    try {
      await invoicesAPI.delete(invoiceId);
      toast({
        title: "Gelukt",
        description: "Factuur succesvol verwijderd"
      });
      fetchInvoices();
    } catch (error) {
      console.error('Fout bij verwijderen factuur:', error);
      toast({
        title: "Fout",
        description: "Kan factuur niet verwijderen",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Facturen laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Facturatie</h1>
          <p className="text-gray-500 mt-1">Beheer offertes, facturen en betalingen</p>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" className="border-gray-300">
            <Download className="w-4 h-4 mr-2" />
            Exporteren
          </Button>
          {canCreate() && <AddInvoiceDialog onSuccess={fetchInvoices} />}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Totaal Gefactureerd</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">€{totalInvoiced.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Totaal Betaald</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">€{totalPaid.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center shadow-lg">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Openstaand</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">€{totalOutstanding.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center shadow-lg">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Verlopen</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{overdueCount}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center shadow-lg">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-lg border-0">
        <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-green-50">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center text-lg">
              <FileText className="w-5 h-5 mr-2 text-blue-600" />
              Alle Facturen & Offertes
            </CardTitle>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Zoek facturen..."
                  className="pl-10 w-64"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-invoices-input"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex border-b bg-gray-50 p-4 space-x-2 overflow-x-auto">
            {statuses.map(status => (
              <Button
                key={status}
                variant={selectedStatus === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedStatus(status)}
                className={selectedStatus === status ? 'bg-gradient-to-r from-blue-600 to-green-600' : ''}
              >
                {getStatusLabel(status)}
              </Button>
            ))}
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Nummer</TableHead>
                <TableHead className="font-semibold">Type</TableHead>
                <TableHead className="font-semibold">Project</TableHead>
                <TableHead className="font-semibold">Klant</TableHead>
                <TableHead className="font-semibold">Datum</TableHead>
                <TableHead className="font-semibold">Vervaldatum</TableHead>
                <TableHead className="font-semibold">Bedrag</TableHead>
                <TableHead className="font-semibold">Betaald</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvoices.map((invoice) => {
                const isQuote = invoice.type === 'quote' || invoice.invoiceNumber?.startsWith('OFF-');
                return (
                <TableRow key={invoice.id} className="hover:bg-gray-50 transition-colors duration-200">
                  <TableCell className="font-medium">{invoice.invoiceNumber}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`${
                      isQuote ? 'bg-purple-50 text-purple-700 border-purple-200' :
                      'bg-blue-50 text-blue-700 border-blue-200'
                    }`}>
                      {isQuote ? 'Offerte' : 'Factuur'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">{invoice.project}</TableCell>
                  <TableCell className="text-sm text-gray-600">{invoice.customer}</TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {new Date(invoice.issueDate).toLocaleDateString('nl-NL')}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {new Date(invoice.dueDate).toLocaleDateString('nl-NL')}
                  </TableCell>
                  <TableCell className="font-semibold">€{invoice.amount.toLocaleString()}</TableCell>
                  <TableCell className="text-green-600 font-medium">€{invoice.paid.toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge className={`${
                      invoice.status === 'paid' ? 'bg-green-100 text-green-700 hover:bg-green-100' :
                      invoice.status === 'partial' ? 'bg-blue-100 text-blue-700 hover:bg-blue-100' :
                      invoice.status === 'overdue' ? 'bg-red-100 text-red-700 hover:bg-red-100' :
                      invoice.status === 'draft' ? 'bg-gray-100 text-gray-700 hover:bg-gray-100' :
                      'bg-yellow-100 text-yellow-700 hover:bg-yellow-100'
                    }`}>
                      {getStatusLabel(invoice.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-8"
                        onClick={() => handleDownload(invoice)}
                        title="Download PDF"
                        data-testid={`download-invoice-${invoice.id}-btn`}
                      >
                        <Download className="w-3 h-3" />
                      </Button>
                      {canEdit() && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-8"
                          onClick={() => openSendDialog(invoice)}
                          title="Versturen"
                          data-testid={`send-invoice-${invoice.id}-btn`}
                        >
                          <Send className="w-3 h-3" />
                        </Button>
                      )}
                      {canDelete() && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-invoice-${invoice.id}-btn`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Factuur Verwijderen</AlertDialogTitle>
                              <AlertDialogDescription>
                                Weet je zeker dat je factuur "{invoice.invoiceNumber}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuleren</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(invoice.id)}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid="confirm-delete-invoice-btn"
                              >
                                Verwijderen
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Send className="w-5 h-5 mr-2 text-blue-600" />
              {selectedInvoice?.invoiceNumber?.startsWith('OFF-') ? 'Offerte Versturen' : 'Factuur Versturen'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                <strong>Nummer:</strong> {selectedInvoice?.invoiceNumber}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Klant:</strong> {selectedInvoice?.customer}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Bedrag:</strong> €{selectedInvoice?.amount?.toLocaleString()}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="recipientEmail">E-mailadres ontvanger</Label>
              <Input
                id="recipientEmail"
                type="email"
                placeholder="klant@voorbeeld.nl"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => setSendDialogOpen(false)}
              >
                Annuleren
              </Button>
              <Button 
                onClick={handleSend}
                disabled={sending}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Versturen...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Versturen
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoicing;
